import type { UserInterface } from '@store/user/interfaces/userInterfaces';
import { isValidHondurasPhone } from '@utils/phoneUtils';

import { i18nInstance } from '@/i18n/instance';

const TAXPAYER_NUMBER_MAX_LENGTH = 35;

// type for i18n messages
export interface I18nMessage {
  key: string; // e.g. 'validation.required'
  labelKey?: string; // e.g. 'fields.personalInfo.fullName'
  values?: Record<string, unknown>; // valores extra para interpolar
}

// Update ValidationRule
export interface ValidationRule {
  required: boolean;
  message: I18nMessage | string;
  validator?: (_value: unknown, _user: UserInterface) => ValidationResult;
  conditional?: ConditionalValidator;
}

export interface ConditionalValidator {
  dependsOn: string;
  condition: (_dependentValue: unknown, _user: UserInterface) => boolean;
  requiredWhen: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

// Helper to resolve message
function resolveRuleMessage(
  msg?: ValidationRule['message'],
): string | undefined {
  if (!msg) return undefined;
  if (typeof msg === 'string') return msg;

  const t = i18nInstance.t.bind(i18nInstance);
  const label = msg.labelKey ? t(msg.labelKey) : undefined;

  return t(msg.key, {
    ...(msg.values ?? {}),
    ...(label ? { field: label } : {}),
  });
}

// Helper function to get nested value from object
export function getNestedValue(
  obj: Record<string, unknown>,
  path: string,
): unknown {
  return path.split('.').reduce((current, key) => {
    return current && typeof current === 'object'
      ? (current as Record<string, unknown>)[key]
      : undefined;
  }, obj as unknown);
}

// Base validators…
export const TypeValidators = {
  string: (value: unknown): ValidationResult => {
    if (typeof value === 'string') {
      return { isValid: value.trim().length > 0 };
    }
    return { isValid: value != null };
  },
  boolean: (value: unknown): ValidationResult => ({
    isValid: typeof value === 'boolean',
  }),
  yesNo: (value: unknown): ValidationResult => ({
    isValid: value === 'yes' || value === 'no',
  }),
  email: (value: unknown): ValidationResult => {
    if (typeof value !== 'string') return { isValid: false };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return { isValid: emailRegex.test(value) };
  },
  phone: (value: unknown): ValidationResult => {
    if (typeof value !== 'string') return { isValid: false };

    // Use Honduras-specific phone validation
    if (isValidHondurasPhone(value)) {
      return { isValid: true };
    }

    // Fallback to general phone validation for international numbers
    const phoneRegex = /^\+?[\d\s\-()]+$/;
    return { isValid: phoneRegex.test(value) && value.length >= 10 };
  },
  array: (value: unknown): ValidationResult => ({
    isValid: Array.isArray(value) && value.length > 0,
  }),
  number: (value: unknown): ValidationResult => {
    // Handle numeric strings from form inputs
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return { isValid: false };
      const num = Number(trimmed);
      return { isValid: !isNaN(num) && isFinite(num) };
    }
    if (typeof value === 'number')
      return { isValid: !isNaN(value) && isFinite(value) };
    return { isValid: false };
  },
  currency: (value: unknown): ValidationResult => {
    // Handle currency strings from form inputs (may include commas, decimals, etc.)
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return { isValid: false };
      const cleaned = trimmed.replace(/[,$\s]/g, '');
      const num = Number(cleaned);
      return { isValid: !isNaN(num) && isFinite(num) && num >= 0 };
    }
    if (typeof value === 'number')
      return { isValid: !isNaN(value) && isFinite(value) && value >= 0 };
    return { isValid: false };
  },

  // Date range validation - ensures end date is not before start date
  dateRange: (startDate: unknown, endDate: unknown): ValidationResult => {
    if (typeof startDate !== 'string' || typeof endDate !== 'string') {
      return { isValid: false };
    }

    // If either date is empty, skip range validation (individual required validation handles this)
    if (!startDate.trim() || !endDate.trim()) {
      return { isValid: true };
    }

    // Parse both DD/MM/YYYY and YYYY-MM-DD formats
    const parseDate = (dateStr: string): Date | null => {
      const trimmed = dateStr.trim();

      // Check for DD/MM/YYYY format
      const ddmmyyyyPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
      const ddmmyyyyMatch = trimmed.match(ddmmyyyyPattern);

      if (ddmmyyyyMatch) {
        const [, day, month, year] = ddmmyyyyMatch;
        const parsedDate = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
        );

        // Verify the date is valid and matches what we parsed
        if (
          parsedDate.getFullYear() === parseInt(year) &&
          parsedDate.getMonth() === parseInt(month) - 1 &&
          parsedDate.getDate() === parseInt(day)
        ) {
          return parsedDate;
        }
        return null;
      }

      // Check for YYYY-MM-DD format (ISO format used by DateInput internally)
      const yyyymmddPattern = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
      const yyyymmddMatch = trimmed.match(yyyymmddPattern);

      if (yyyymmddMatch) {
        const [, year, month, day] = yyyymmddMatch;
        const parsedDate = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
        );

        // Verify the date is valid and matches what we parsed
        if (
          parsedDate.getFullYear() === parseInt(year) &&
          parsedDate.getMonth() === parseInt(month) - 1 &&
          parsedDate.getDate() === parseInt(day)
        ) {
          return parsedDate;
        }
        return null;
      }

      return null;
    };

    const start = parseDate(startDate);
    const end = parseDate(endDate);

    // Check if dates are valid
    if (!start || !end) {
      return { isValid: false };
    }

    // End date should not be before start date
    return { isValid: end >= start };
  },
};

// Conditional validators for complex scenarios
export const ConditionalValidators = {
  // FATCA field validation
  fatcaField: (_fieldPath: string): ConditionalValidator => ({
    dependsOn: 'fatcaInfo.applies',
    condition: applies => applies === true,
    requiredWhen: true,
  }),

  // Extra nationality field validation
  extraNationalityField: (_fieldPath: string): ConditionalValidator => ({
    dependsOn: 'address.extraNationality.applies',
    condition: applies => applies === true,
    requiredWhen: true,
  }),

  // Taxpayer number - only required if hasTaxpayerNumber is 'yes'
  taxpayerNumber: (): ConditionalValidator => ({
    dependsOn: 'address.extraNationality.hasTaxpayerNumber',
    condition: value => value === 'yes',
    requiredWhen: true,
  }),

  // taxpayerNumberReason is required if hasTaxpayerNumber is 'no'
  noTaxpayerField: (): ConditionalValidator => ({
    dependsOn: 'address.extraNationality.hasTaxpayerNumber',
    condition: value => value === 'no',
    requiredWhen: true,
  }),

  // PEP fields - only required if isPEP is true
  pepField: (): ConditionalValidator => ({
    dependsOn: 'jobInfo.pepInfo.isPEP',
    condition: isPEP => isPEP === true,
    requiredWhen: true,
  }),

  // Family member PEP fields - only required if familyMemberPEP is true
  familyMemberPepField: (): ConditionalValidator => ({
    dependsOn: 'jobInfo.pepInfo.familyMemberPEP',
    condition: familyMemberPEP => familyMemberPEP === true,
    requiredWhen: true,
  }),

  // professionalInfo.retiredCountry is required if occupation is 'Jubilado/Pensionado'
  occupationIs: (expected: string): ConditionalValidator => ({
    dependsOn: 'professionalInfo.occupation',
    condition: dependentValue => dependentValue === expected,
    requiredWhen: true,
  }),
};

// Specialized field validators
export const FieldValidators = {
  // FATCA yes/no fields
  fatcaYesNo: (value: unknown, user: UserInterface): ValidationResult => {
    if (!user.fatcaInfo?.applies) return { isValid: true };
    return TypeValidators.yesNo(value);
  },

  // FATCA string fields (TIN, tax country)
  fatcaString: (value: unknown, user: UserInterface): ValidationResult => {
    if (!user.fatcaInfo?.applies) return { isValid: true };
    return TypeValidators.string(value);
  },
  fatcaTIN: (value: unknown, user: UserInterface): ValidationResult => {
    if (!user.fatcaInfo?.applies) return { isValid: true };
    if (typeof value !== 'string') return { isValid: false };
    const pattern = /^(?:\d{9}|\d{3}-\d{2}-\d{4})$/;
    const ok = pattern.test(value.trim());
    const digits = value.replace(/\D/g, '');
    return { isValid: ok && digits.length === 9 };
  },

  // Extra nationality yes/no fields
  extraNationalityYesNo: (
    value: unknown,
    user: UserInterface,
  ): ValidationResult => {
    if (!user.address?.extraNationality?.applies) return { isValid: true };
    return TypeValidators.yesNo(value);
  },

  // Extra nationality string fields
  extraNationalityString: (
    value: unknown,
    user: UserInterface,
  ): ValidationResult => {
    if (!user.address?.extraNationality?.applies) return { isValid: true };
    return TypeValidators.string(value);
  },

  // Taxpayer number - conditional on hasTaxpayerNumber
  taxpayerNumber: (value: unknown, user: UserInterface): ValidationResult => {
    if (!user.address?.extraNationality?.applies) return { isValid: true };
    if (user.address?.extraNationality?.hasTaxpayerNumber !== 'yes')
      return { isValid: true };
    if (typeof value !== 'string') return { isValid: false };
    const v = value.trim();
    const regex = /^[A-Za-z0-9-]+$/;
    return {
      isValid:
        v.length > 0 && v.length <= TAXPAYER_NUMBER_MAX_LENGTH && regex.test(v),
    };
  },
  noTaxpayerExplanation: (
    value: unknown,
    user: UserInterface,
  ): ValidationResult => {
    if (!user.address?.extraNationality?.applies) return { isValid: true };
    if (user.address?.extraNationality?.hasTaxpayerNumber !== 'no')
      return { isValid: true };
    if (typeof value !== 'string') return { isValid: false };
    const v = value.trim();
    const regex = /^[-A-Za-z0-9\s.,]+$/;
    return { isValid: v.length > 0 && v.length <= 80 && regex.test(v) };
  },
  mustBeTrue: (value: unknown): ValidationResult => ({
    isValid: value === true,
  }),

  // Combined PEP end date validation - handles both required and date range
  pepEndDateValidation: (
    value: unknown,
    user: UserInterface,
  ): ValidationResult => {
    if (!user.jobInfo?.pepInfo?.isPEP) return { isValid: true };

    // First check if the field is required and not empty
    const stringValidation = TypeValidators.string(value);
    if (!stringValidation.isValid) {
      return { isValid: false }; // Will use the rule's default message "End date is required"
    }

    // Then check date range if both dates are present
    const startDate = user.jobInfo.pepInfo.pepPositionStartDate;
    const endDate = user.jobInfo.pepInfo.pepPositionEndDate;

    if (startDate && startDate.trim() && endDate && endDate.trim()) {
      const rangeValidation = TypeValidators.dateRange(startDate, endDate);
      if (!rangeValidation.isValid) {
        // Use the internationalized message for date range error
        const t = i18nInstance.t.bind(i18nInstance);
        return {
          isValid: false,
          message: t('validation.jobInfo.pepInfo.pepPositionEndDateRange'),
        };
      }
    }

    return { isValid: true };
  },
};

// Main field validation function
export function validateField(
  fieldPath: string,
  rule: ValidationRule,
  user: UserInterface,
): ValidationResult {
  const value = getNestedValue(
    user as unknown as Record<string, unknown>,
    fieldPath,
  );

  // Check conditional validation first
  if (rule.conditional) {
    const dependentValue = getNestedValue(
      user as unknown as Record<string, unknown>,
      rule.conditional.dependsOn,
    );
    const conditionMet = rule.conditional.condition(dependentValue, user);
    if (!conditionMet && !rule.conditional.requiredWhen)
      return { isValid: true };
    if (!conditionMet && rule.conditional.requiredWhen)
      return { isValid: true };
  }

  if (rule.required) {
    if (rule.validator) {
      const result = rule.validator(value, user);
      return {
        isValid: result.isValid,
        message: result.isValid
          ? undefined
          : result.message || resolveRuleMessage(rule.message),
      };
    }
    const result = TypeValidators.string(value);
    return {
      isValid: result.isValid,
      message: result.isValid ? undefined : resolveRuleMessage(rule.message),
    };
  }

  if (value != null && rule.validator) {
    const result = rule.validator(value, user);
    return {
      isValid: result.isValid,
      message: result.isValid
        ? undefined
        : result.message || resolveRuleMessage(rule.message),
    };
  }

  return { isValid: true };
}
